/**
 * Tabs that look like the tabs on a folder.
 *
 * The previous style was a word with a line under it, which reads as a link
 * until you notice the line — nothing about it says "these are the faces of
 * one thing and you are looking at this one". A folder tab does: it is a
 * surface, it is in front, and the one you are on is joined to the panel
 * beneath it while the others are behind.
 *
 * An `sx` object rather than a component, so the screens already using MUI's
 * <Tabs> keep their markup and their behaviour — the keyboard handling, the
 * value API — and only change how they look.
 *
 *   <Tabs value={tab} onChange={…} sx={folderTabs}>
 *
 * The indicator is hidden: the selected tab merging into the panel IS the
 * indicator, and a line as well would be saying it twice.
 *
 * Two rules keep the row still, which matters more than it sounds: a row of
 * tabs that shifts when you click one makes you doubt you hit the thing you
 * aimed at. Nothing about being selected may change a tab's WIDTH, and the
 * row must fit without becoming a scroller. See the notes below.
 */
const folderTabs = {
  minHeight: 42,
  // The rail the tabs sit on, and which the selected one breaks through.
  borderBottom: '2px solid',
  borderColor: 'divider',
  '& .MuiTabs-indicator': { display: 'none' },
  // The selected tab hangs 2px below the row to cover the rail. The scroller
  // clips that away by default, taking the join with it.
  '& .MuiTabs-scroller': { overflow: 'visible' },
  '& .MuiTabs-flexContainer': { gap: '4px', alignItems: 'flex-end' },

  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 14,
    minHeight: 38,
    px: '16px',
    /**
     * As wide as the label needs and no wider, and able to give width back
     * when the row is tight. MUI's default is a 90px floor that never
     * shrinks; a few tabs of ordinary length then outgrow a half-width
     * column, and the row turns into a strip that scrolls itself sideways
     * every time you change tab.
     */
    minWidth: 0,
    flexShrink: 1,
    color: 'text.secondary',
    borderRadius: '12px 12px 0 0',
    bgcolor: 'action.hover',
    border: '1px solid',
    borderColor: 'divider',
    borderBottom: 'none',
    transition: 'background-color 140ms ease, color 140ms ease',
    '&:hover': { bgcolor: 'action.selected', color: 'text.primary' },
  },

  /**
   * Selected: same width, same weight — only the colour and the surface
   * change. Bolding the selected tab is the obvious way to mark it and the
   * reason a row of tabs creeps left and right as you click along it: bold
   * text is wider, so the tab you just chose grows and pushes its neighbours.
   * Being the one white tab joined to the panel is already the strongest
   * signal on the row; it doesn't need the weight as well.
   */
  '& .MuiTab-root.Mui-selected': {
    color: 'text.primary',
    minHeight: 42,
    bgcolor: 'background.paper',
    // Pulled down over the rail and capped with the panel's own colour, so
    // the tab and the panel below are one surface with no line between them
    // — which is the whole point of a folder tab.
    mb: '-2px',
    borderBottom: '2px solid',
    borderBottomColor: 'background.paper',
  },
};

export default folderTabs;
